/**
 * LiminalAtrium — Level 0, Tutorial.
 *
 * A dusty Victorian sunroom under renovation. One co-op gate puzzle
 * teaches all six base abilities (see `TutorialGatePuzzle`).
 *
 * This file is the scene shell only: boot, lighting, post-processing,
 * ground/platforms (physics), shadow zones, environment model, the
 * readability cue layer, and the opening cutscene. All puzzle logic and
 * all puzzle interactives (plate, dial, crystal, portal) live in
 * `../levels/puzzles/liminal-atrium/TutorialGate.ts`.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { SpotLight } from "@babylonjs/core/Lights/spotLight";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { ImageProcessingConfiguration } from "@babylonjs/core/Materials/imageProcessingConfiguration";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { applyVisualProfile, LIMINAL_ATRIUM_PROFILE } from "../levels";
import {
  ParallaxBackground,
  paintGradient,
  paintTreeSilhouette,
} from "../systems/ParallaxBackground";
import { getOrMakeNoiseBump } from "../systems/ProceduralNoise";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import "@babylonjs/loaders/glTF";
import { getAssetManager, InstantiatedModel } from "../systems/AssetManager";
import { freezeStaticMeshes, DEFAULT_STATIC_EXCLUDES } from "../levels/staticOptimization";
import { tagAs } from "../levels";
import { PhysicsSystem } from "../systems/PhysicsSystem";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { AudioManager } from "../systems/AudioManager";
import { PressurePlate } from "../entities/interactives/PressurePlate";
import { Dial } from "../entities/interactives/Dial";
import { CrystalReceiver } from "../entities/interactives/CrystalReceiver";
import { Portal } from "../entities/interactives/Portal";
import { Gramophone } from "../entities/interactives/Gramophone";
import { GuardianDog } from "../entities/npcs/GuardianDog";
import { InteractiveInfo } from "../abilities/minna/AuraSight";
import { ShadowZoneInfo } from "../abilities/minna/ShadowSlip";
import { ATRIUM, COLORS, WORLD_DEBUG } from "../config/GameConfig";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { CutsceneSystem, CutsceneStep } from "../systems/CutsceneSystem";
import { OPENING_CUTSCENE, LEVEL_INTRO, CONTROL_HINTS } from "../config/dialogue";
import { setupBaseLevel, BaseLevelContext } from "./BaseLevel";
import type { GlowLayer } from "@babylonjs/core/Layers/glowLayer";
import { LIMINAL_ATRIUM_LAYOUT } from "./LiminalAtriumLayout";
import { LIMINAL_ATRIUM_PLATFORMS, platformDepth, platformHeight, type PlatformDef } from "../levels/layouts";
import type { PuzzleContext, PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import {
  TutorialGatePuzzle,
  type TutorialReadabilitySnapshot,
} from "../levels/puzzles/liminal-atrium/TutorialGate";

export interface LiminalAtriumContext {
  physicsSystem: PhysicsSystem;
  cameraSystem: CameraSystem;
  inputManager: InputManager;
  minna: import("../entities/players/Minna").Minna;
  basil: import("../entities/players/Basil").Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  audioManager: AudioManager;
  cutsceneSystem: CutsceneSystem;
}

interface LiminalReadabilityCues {
  update(snapshot: TutorialReadabilitySnapshot, auraSightActive: boolean): void;
}

/**
 * Set up the Liminal Atrium scene.
 * Call after the engine and scene are created and physics is initialized.
 */
export async function setupLiminalAtrium(scene: Scene): Promise<LiminalAtriumContext> {
  // --- Base level setup (physics, players, abilities, camera, audio, input, cutscene) ---
  const base = await setupBaseLevel(scene, {
    levelKey: "LiminalAtrium",
    // These are overwritten a few lines later by applyVisualProfile →
    // LIMINAL_ATRIUM_PROFILE, but we keep them aligned so the base-level
    // boot frame and the post-profile frame match visually.
    clearColor: new Color4(0.06, 0.04, 0.09, 1.0),
    fogColor: new Color3(0.23, 0.29, 0.25),
    fogDensity: 0.003,
    // Both cats land on platform2 (center x=-1, width 2.5 → spans
    // -2.25..0.25). The earlier spawn put Basil at x=1.1 which is the
    // open gap between platform2 and platform3, so every level load he
    // dropped straight through to the ground and got stranded.
    // ~2.0u apart (within platform2's -2.25..0.25 span) so the two cat
    // models don't visually interpenetrate at spawn. The old 1.2u gap
    // let the rendered models overlap on the windowsill.
    minnaSpawn: new Vector3(-1.5, 3.2, 0),
    basilSpawn: new Vector3(-0.4, 3.2, 0),
    // Tutorial-easy Turn-Table Touch: wider window, slower marker, no
    // combo speedup. Levels 1-3 keep the default narrow tuning.
    turnTableTouchConfig: { ...ATRIUM.ABILITY_TUNING.TURN_TABLE_TOUCH, visualRadius: 1.15, visualDepthOffset: 1.1 },
    auraSightVisualConfig: { glowIntensity: 0.32, threadRadius: 0.012, threadBrightness: 0.4 },
    calmingResonanceVisualConfig: { ringThickness: 0.008, ringOpacity: 0.15 },
    abilityVfxVisualConfig: { particleDensityScale: .25, particleSizeScale: .65 },
    resonanceStreamOpacity: 0.08,
  });

  const {
    physics: physicsSystem, minna, basil, minnaAbilities, basilAbilities,
    auraSight, shadowSlip, calmingResonance, turnTableTouch, pneumaticAnchor,
    camera: cameraSystem, audioManager, cutsceneSystem, inputManager,
  } = base;

  cameraSystem.setBounds({
    minX: -9,
    maxX: 9,
    minY: 2.0,
    maxY: 4.0,
  });

  // --- Visual profile: lights, shadows, post-process, glow, fog, clearColor ---
  const visualHandles = applyVisualProfile(scene, LIMINAL_ATRIUM_PROFILE, {
    glowLayer: base.getGlowLayer(),
  });
  base.addDisposeCallback(() => visualHandles.dispose());

  // Switch the pipeline to ACES tonemapping. The default linear tonemap
  // was clipping the warmer key light to white; ACES preserves the
  // shoulder so the god-ray reads as a warm beam rather than a blown-out
  // highlight against the darker Victorian furniture.
  // Low-fi mode leaves image processing disabled (imageProcessing is null).
  const imageProcessing = visualHandles.pipeline.imageProcessing;
  if (imageProcessing) {
    imageProcessing.toneMappingEnabled = true;
    imageProcessing.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  }

  const glowLayer = visualHandles.glowLayer;
  const shadowGen = visualHandles.shadowGenerator!;
  base.shadowGenerator = shadowGen;

  // DirectionalLight defaults to a position one direction-vector away from
  // the origin. That put the elevated cats behind the shadow near plane
  // (measured clip z=-1.00048). Position the source above this finite room.
  visualHandles.keyLight.position.set(-10, 24, -16);
  visualHandles.keyLight.shadowMinZ = 0.1;
  visualHandles.keyLight.shadowMaxZ = 60;

  // Warm room bounce complements the garden-facing key.
  scene.ambientColor = new Color3(0.10, 0.08, 0.06);

  // --- Liminal-specific SpotLights ---
  // A hero golden god-ray cone pouring in from the upper-left window,
  // matching the concept art. This is the single biggest visual cue that
  // the room is a sunroom and not a void.
  const godRay = new SpotLight(
    "godRay",
    new Vector3(-7, 6, -2),
    new Vector3(0.2, -1, 0.3),
    Math.PI / 5,
    2,
    scene,
  );
  godRay.diffuse = new Color3(1.0, 0.88, 0.55);
  godRay.specular = new Color3(1.0, 0.9, 0.6);
  godRay.intensity = 4.5;
  godRay.range = 14;

  // Subtle cool-green beam that accents the portal side of the room.
  const bioLight = new SpotLight(
    "bioAccent",
    new Vector3(-7, 5, -3),
    new Vector3(0.1, -1, 0.4),
    Math.PI / 6,
    4,
    scene,
  );
  bioLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
  bioLight.intensity = 0.18;
  bioLight.range = 6;

  // Mirror bioluminescent beam on the far portal side so the green tint
  // reads on both halves of the playfield.
  const portalBio = new SpotLight(
    "portalBioAccent",
    new Vector3(7, 5, -3),
    new Vector3(-0.15, -1, 0.4),
    Math.PI / 6,
    4,
    scene,
  );
  portalBio.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
  portalBio.intensity = 0.22;
  portalBio.range = 7;

  // Add players as shadow casters
  // Descendant registration is a snapshot: doing this before the asynchronous
  // GLBs finish only registers the invisible capsules, never their skins.
  await Promise.all([minna.modelReady, basil.modelReady]);
  shadowGen.addShadowCaster(minna.mesh);
  shadowGen.addShadowCaster(basil.mesh);

  // Low, neutral room bounce separates black fur from the detailed garden.
  // Scope it to the loaded cats: it must not wash out the walnut or windows.
  for (const cat of [minna, basil]) {
    const bounce = new PointLight(`${cat.mesh.name}_atriumBounce`, new Vector3(0, 0.8, 2), scene);
    bounce.parent = cat.mesh;
    bounce.diffuse = new Color3(0.72, 0.78, 0.73);
    bounce.specular = new Color3(0.2, 0.22, 0.2);
    bounce.intensity = 1.2;
    bounce.range = 4;
    bounce.renderPriority = 25;
    bounce.includedOnlyMeshes = [cat.mesh, ...cat.mesh.getChildMeshes()];
  }

  // --- Parallax "garden through the window" (Phase C3) ---
  // Atrium is enclosed, so parallax reads only through the leaded-glass
  // bays behind the playfield. Two faint layers: a warm dawn-gradient sky
  // and a distant tree silhouette. Very subtle alphas so the player's
  // focus stays on the puzzle, not a moving backdrop.
  const atriumParallax = new ParallaxBackground(scene, cameraSystem.camera, [
    {
      name: "atriumFarSky",
      z: -24,
      scrollFactor: 0.9,
      width: 60,
      height: 24,
      yOffset: 5.5,
      alpha: 0.45,
      paint: paintGradient("#3b2d3e", "#6b4a3a"),
    },
    {
      name: "atriumGarden",
      z: -15,
      scrollFactor: 0.6,
      width: 52,
      height: 14,
      yOffset: 2.8,
      alpha: 0.45,
      paint: paintTreeSilhouette("#1b1224", 0.35, 0.68, 28),
    },
  ]);
  base.addDisposeCallback(
    base.registerSafeBeforeRender("atriumParallax", () => atriumParallax.update()),
  );
  base.addDisposeCallback(() => atriumParallax.dispose());

  // --- Ground with PBR material ---
  // Dark mahogany PBR, matching the Victorian sunroom concept. The
  // procedural ground is normally hidden once atrium_env.glb's floor
  // loads, but keep the material tuned so the fallback path looks right
  // if the env GLB ever fails to load or a future change re-exposes it.
  const ground = physicsSystem.createGround(ATRIUM.GROUND_WIDTH, ATRIUM.GROUND_DEPTH);
  const groundMat = new PBRMaterial("groundMat", scene);
  groundMat.albedoColor = new Color3(0.20, 0.12, 0.07);
  groundMat.metallic = 0.0;
  groundMat.roughness = 0.78;
  groundMat.emissiveColor = new Color3(0.01, 0.005, 0.015);
  // C6: bump + roughness variance so the floor isn't perfectly flat
  // under the god-ray. Noise texture is cached per key; tiled 8× so the
  // grain is planks-sized rather than a wall-sized smudge.
  const atriumFloorNoise = getOrMakeNoiseBump(scene, "atrium_floor_mahogany", 0.5);
  atriumFloorNoise.uScale = 8;
  atriumFloorNoise.vScale = 8;
  groundMat.bumpTexture = atriumFloorNoise;
  groundMat.bumpTexture.level = 0.35;
  if (ground) {
    ground.material = groundMat;
    ground.receiveShadows = true;
  }

  // --- Platforms with PBR wood material and shadows ---
  const platformMat = new PBRMaterial("platformMat", scene);
  platformMat.albedoColor = new Color3(0.35, 0.22, 0.12); // Dark wood
  platformMat.metallic = 0.0;
  platformMat.roughness = 0.8;
  platformMat.emissiveColor = new Color3(0.02, 0.01, 0.005);

  // Geometry (and why it is where it is) lives in the layout data so the
  // reachability lint can check it: src-babylon/levels/layouts/.
  const platforms = LIMINAL_ATRIUM_PLATFORMS.platforms.map((def) =>
    physicsSystem.createPlatform(
      def.name,
      def.w,
      platformHeight(def),
      platformDepth(def),
      new Vector3(def.x, def.y, 0),
    ),
  );
  for (const plat of platforms) {
    plat.material = platformMat;
    plat.receiveShadows = true;
    shadowGen.addShadowCaster(plat);
  }

  // --- Load 3D environment model (visual shell over physics colliders) ---
  // Env/hero GLB failures fall back to procedural geometry, but must not
  // stay silent (Q-036f): surface ONE player-facing toast per scene load
  // so stand-in shapes are never an unexplained mystery. (Interactive
  // GLBs report through InteractiveObject's own once-per-path toast.)
  let artFailureToastShown = false;
  const reportArtLoadFailure = () => {
    if (artFailureToastShown) return;
    artFailureToastShown = true;
    gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "Some level art failed to load — showing stand-in shapes.",
    });
  };

  let envInstance: InstantiatedModel | null = null;
  const envMeshes: AbstractMesh[] = [];
  try {
    envInstance = await getAssetManager().instantiateModel(
      scene,
      "/assets/models/environment/atrium_sunroom.glb",
    );

    // Position/scale the env root to align with physics colliders
    // Game world: ~30 units wide, ground at Y=0, platforms Y=1.5..6
    const envRoot = envInstance.rootNodes[0];
    if (envRoot instanceof TransformNode) {
      envRoot.position.set(0, 0, 0);
      // Tag the loaded environment root as production art so the audit
      // can distinguish it from the procedural ground/platform colliders.
      tagAs(envRoot, "final");
    }

    // Enable shadow receiving on all env meshes and tag them as final art.
    for (const mesh of envInstance.meshes) {
      mesh.receiveShadows = true;
      envMeshes.push(mesh);
      tagAs(mesh, "final");
      if (mesh.name.includes("_furniture_assembly") || /platform\d+_top$/.test(mesh.name)) {
        shadowGen.addShadowCaster(mesh, false);
      }
    }

    // The authored replacement owns its complete room/garden depth. Dispose
    // the fallback backdrop rather than retaining hidden overlapping planes.
    atriumParallax.dispose();
  } catch (err) {
    console.warn("[LiminalAtrium] Failed to load environment model, using procedural geometry:", err);
    reportArtLoadFailure();
  }

  // Keep colliders for simulation, but render the measured furniture surfaces
  // from the authored room. The fallback still renders supported platforms.
  if (envInstance) {
    if (ground) {
      ground.isVisible = false;
    }
    // build_atrium.py reads the authoritative platform data and authors a
    // supported furniture surface at every collider top, including the exit.
    const SUNROOM_COVERED = new Set(LIMINAL_ATRIUM_PLATFORMS.platforms.map(p => p.name));
    for (const plat of platforms) {
      if (SUNROOM_COVERED.has(plat.name)) {
        plat.isVisible = false;
      }
    }
  }

  // Visible platforms are parlour furniture rather than stand-in boxes:
  // dark wood with a brass band and rivets on the front face (Level 3's
  // finish) and two legs down to the floor along the back edge, behind
  // the cats, so they read as consoles instead of floating slabs.
  const platformTrimMat = new PBRMaterial("atriumPlatformTrimMat", scene);
  platformTrimMat.albedoColor = Color3.FromHexString(COLORS.BRASS).scale(0.85);
  platformTrimMat.metallic = 0.75;
  platformTrimMat.roughness = 0.35;
  platformTrimMat.emissiveColor = new Color3(0.06, 0.04, 0.012);
  LIMINAL_ATRIUM_PLATFORMS.platforms.forEach((def, i) => {
    const plat = platforms[i];
    if (plat.isVisible) dressVisiblePlatform(scene, plat, def, platformMat, platformTrimMat, platforms);
  });

  // Clean up the instantiated clones on scene dispose.
  if (envInstance) {
    const instance = envInstance;
    base.addDisposeCallback(() => {
      instance.dispose();
    });
  }

  // --- Hero props (Milestone B2) ─────────────────────────────────
  // The layout (positions, scale, gear speeds, dog wake zone, shadow
  // zones) is fully declarative in `LiminalAtriumLayout.ts`. This
  // function reads it and wires the Babylon side.
  const layout = LIMINAL_ATRIUM_LAYOUT;

  // Collect the individual gear meshes so the game loop can spin them
  // on sceneTime without a per-frame name lookup.
  const animatedGears: { mesh: AbstractMesh; speed: number }[] = [];

  // NOTE: the legacy `dogWakeCooldown` / `layout.dogWake` rug-trigger
  // was removed when the Guardian Dog was promoted to a real NPC
  // (`GuardianDog`). That trigger respawned Minna whenever she crossed
  // an invisible rug volume — the single most canonical concurrent-
  // puzzle teaching beat from companion-book p.7 was hidden behind a
  // respawn. The NPC replacement listens for footsteps (sound cone),
  // the Gramophone lullaby, and Minna's Purr. See `GuardianDog.ts`.

  const heroInstances: InstantiatedModel[] = [];
  for (const spec of layout.heroProps) {
    // The new shell owns its floor, wall apertures and actual window frames.
    // Retain the old standalone architecture only as the missing-art fallback.
    if (envInstance && ["sunroomShell", "window_L", "window_C", "window_R", "atriumFloor"].includes(spec.name)) continue;
    try {
      const inst = await getAssetManager().instantiateModel(scene, spec.path);
      const root = inst.rootNodes[0];
      if (root instanceof TransformNode) {
        root.position.copyFrom(spec.position);
        if (spec.rotationY !== undefined) {
          root.rotation = new Vector3(0, spec.rotationY, 0);
        }
        root.scaling.setAll(spec.scale ?? 1.0);
        root.name = `hero_${spec.name}`;
        tagAs(root, "final");
      }
      for (const mesh of inst.meshes) {
        mesh.receiveShadows = true;
        tagAs(mesh, "final");
        if (spec.castShadows !== false) {
          shadowGen.addShadowCaster(mesh);
        }
        // The authored window's arch top ("Win_Arch") is a solid filled
        // 4.2u disc, not a fanlight — in the gameplay camera it loomed
        // over every window as a giant featureless dark ball (the
        // worst-read artifact in the 2026-07-10 visual review, Q-028;
        // runtime-diagnosed via scene bisection). Hide it until the
        // asset is re-authored with a real glazed fan; the window
        // composition ends cleanly at the top beam without it.
        if (mesh.name.includes("Win_Arch")) {
          mesh.isVisible = false;
        }
        // Register the gearbox gears for the ambient spin loop. The
        // speeds come from layout.gearSpin so tuning doesn't require
        // editing this file.
        if (spec.name === "gearbox_L" || spec.name === "gearbox_R") {
          for (const rule of layout.gearSpin) {
            if (rule.nameSuffix.test(mesh.name)) {
              animatedGears.push({ mesh, speed: rule.speed });
              break;
            }
          }
        }
      }
      heroInstances.push(inst);
    } catch (err) {
      console.warn("[LiminalAtrium] Failed to load hero prop %s from %s:", spec.name, spec.path, err);
      reportArtLoadFailure();
    }
  }
  // Freeze static hero props. The Atrium layout has gearboxes whose
  // inner gears spin on every frame — the DEFAULT_STATIC_EXCLUDES
  // "gear" pattern skips those so the ambient animation continues.
  for (const inst of heroInstances) {
    freezeStaticMeshes(inst.meshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });
  }
  base.addDisposeCallback(() => {
    for (const inst of heroInstances) inst.dispose();
  });

  // --- Rusted gramophone (canon p.7 concurrent-puzzle thesis beat) ───
  // The gramophone is now a Basil-operable `InteractiveObject`. Basil
  // winds it with `,` while inside GRAMOPHONE_INTERACT_RADIUS; the
  // sleepy melody plays for ~3.5s and the GuardianDog NPC collapses
  // back to sleep in response. Positioned on the right half of the
  // atrium so the player has to split the cats — Minna approaching
  // the dog on the left while Basil runs to the gramophone on the
  // right — to make the "concurrent puzzle" beat legible on the very
  // first level.
  const gramophone = new Gramophone(scene, {
    key: "atriumGramophone",
    position: new Vector3(2.4, 0.45, -0.6),
    label: "Rusted Gramophone",
  });
  for (const mesh of gramophone.rootMesh.getChildMeshes()) {
    shadowGen.addShadowCaster(mesh);
  }
  base.addDisposeCallback(() => gramophone.dispose());

  // --- Guardian Dog NPC (canon p.7 sleeping-dog concurrent-puzzle) ───
  // Placed mid-corridor between the center platforms (x ≈ -6.5) and
  // the far-left platform5 that leads to the Portal. Both cats must
  // cross the dog's sound cone to reach the portal; the only safe
  // passages are while the gramophone's lullaby is playing OR while
  // Minna's Calming Resonance envelopes the dog. The old invisible
  // rug-trigger at this position has been removed — see the
  // comment block above `dogWakeCooldown` for history.
  const guardianDog = new GuardianDog(scene, {
    key: "atriumGuardianDog",
    position: new Vector3(-6.5, 0.5, -0.3),
    // Face +X (toward the center playfield) so the sight cone covers
    // the approach from platform1 / the gearbox corridor. Rotation 0
    // is +X; the NPC loader rotates the imported mesh by this value.
    facingY: 0,
    soundRadius: 2.8,
    sightRange: 4.2,
  });
  const gramophoneDogUnsub = guardianDog.listenForGramophone();
  base.addUnsub(gramophoneDogUnsub);
  base.addDisposeCallback(() => guardianDog.dispose());

  // --- Shadow Zones ---
  const shadowZones = createShadowZones(scene);
  shadowSlip.registerShadowZones(shadowZones);

  // ─── Puzzle Modules ──────────────────────────────────────────────

  const puzzles: PuzzleModule[] = [
    new TutorialGatePuzzle(),
  ];

  // Level-scoped shared puzzle state map.
  const levelState = new Map<string, PuzzleState>();

  // AuraSight interactive collection — puzzles contribute via
  // `ctx.registerInteractive`, then the level shell registers them with
  // the ability once setup finishes.
  const allInteractives: InteractiveInfo[] = [];

  const puzzleCtx: PuzzleContext = {
    scene,
    physicsSystem,
    glowLayer,
    shadowGenerator: shadowGen,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    audioManager,
    cutsceneSystem,
    gameEvents,
    levelState,
    registerInteractive: (info) => {
      allInteractives.push(info);
    },
    addUnsub: (unsub) => base.addUnsub(unsub),
    getSoloMode: () => inputManager.soloMode,
  };

  // Setup all puzzles sequentially (single module for Atrium, but the
  // pattern matches the other levels so future tutorial puzzles can drop
  // in without restructuring).
  for (const puzzle of puzzles) {
    await puzzle.setup(puzzleCtx);
    levelState.set(puzzle.id, puzzle.getState());
  }

  // Expose per-puzzle modules so `scene.metadata.level.getQAHints()` can
  // aggregate across them — consumed by the hybrid `moveToTarget` /
  // `traverse` / `activateNear` / `waitForEvent` primitives in
  // `tools/qa/playthrough-record.mjs`.
  const liminalLevelHandle = scene.metadata?.level as
    | {
        registerPuzzles?: (
          puzzles: PuzzleModule[],
          levelState: Map<string, PuzzleState>,
        ) => void;
      }
    | undefined;
  liminalLevelHandle?.registerPuzzles?.(puzzles, levelState);

  // Expose loaded puzzle IDs for runtime parity testing against the
  // level's MechanicContract (see tests/integration/puzzle-id-parity.spec.ts).
  // Register AuraSight interactives. Liminal Atrium chains plate -> dial
  // -> crystal -> portal, which is the insertion order used by the
  // TutorialGate puzzle module.
  auraSight.registerInteractives(allInteractives);
  if (allInteractives.length >= 4) {
    auraSight.registerConnections([
      [allInteractives[0], allInteractives[1]], // plate -> dial
      [allInteractives[1], allInteractives[2]], // dial -> crystal
      [allInteractives[2], allInteractives[3]], // crystal -> portal
    ]);
  }

  // --- Readability cue layer ---
  // The cues depend on the puzzle module's interactive meshes so they
  // must be created after puzzle setup. The TutorialGate puzzle exposes
  // its plate/dial/crystal/portal as public fields for this reason.
  const tutorialGate = puzzles[0] as TutorialGatePuzzle;

  // Pre-compile the portal VFX shader now, while the opening cutscene
  // is still fading in, so open() later doesn't hitch the main thread
  // for ~300ms right as the player expects input to respond. Gemini
  // 3.1-pro fluidity review caught a 362ms slow-input at the portal
  // reveal moment; this prewarm eliminates the shader-compile cost.
  tutorialGate.portal.preWarmVfx();

  const readabilityCues = createLiminalReadabilityCues(scene, base, {
    platforms,
    pressurePlate: tutorialGate.pressurePlate,
    dial: tutorialGate.dial,
    crystalReceiver: tutorialGate.crystalReceiver,
    portal: tutorialGate.portal,
    hasEnvironment: envInstance !== null,
  });

  scene.metadata = {
    ...(scene.metadata ?? {}),
    loadedPuzzleIds: puzzles.map((p) => p.id),
    // `liminalDebug` is retained for injection hooks that don't fit the
    // getInternals contract (setStage, triggerReadabilityCrash). For
    // *inspection* of the live puzzle state, prefer the portable path:
    //   scene.metadata.level.getPuzzleInternals()
    // which TutorialGate wires up via its setPuzzle call and which the
    // bridge exposes as the `get_puzzle_internals` MCP verb. New levels
    // should implement `getInternals` on their puzzle instead of adding
    // level-specific metadata entries.
    ...(import.meta.env.DEV ? {
      liminalDebug: {
        setStage: (stage: TutorialReadabilitySnapshot["step"]) => {
          tutorialGate.debugSetReadabilityStage(stage);
        },
        getSnapshot: () => tutorialGate.getReadabilitySnapshot(),
        triggerReadabilityCrash: () => {
          (scene.metadata as Record<string, any>).__forceLiminalReadabilityCrash = true;
        },
      },
    } : {}),
  };

  // --- Tag procedural interactive meshes ---
  // The plate/dial/crystal/portal classes build their visual bodies
  // programmatically and don't go through the tagAs() path themselves.
  // Sweep any mesh matching a known interactive-name prefix that is still
  // untagged and mark it as modular-final art so the placeholder ledger
  // matches runtime composition.
  const interactiveNamePrefixes = [
    /^pressurePlate_/,
    /^dial_/,
    /^crystal_/,
    /^portal_/,
    /^interactive_.*_highlight$/,
  ];
  for (const mesh of scene.meshes) {
    if (mesh.metadata?.placeholderTag) continue;
    if (interactiveNamePrefixes.some((re) => re.test(mesh.name))) {
      tagAs(mesh, "modular-final");
    }
  }

  // --- Dispose puzzles on scene teardown ---
  base.addDisposeCallback(() => {
    for (const puzzle of puzzles) {
      try { puzzle.dispose(); } catch (e) { console.warn("Puzzle dispose error:", e); }
    }
  });

  // Once-per-session gramophone discoverability toast. Fires the first
  // time Basil crosses into interact range so the player learns the
  // `,` prompt without the tutorial having to pause anything.
  const gramoToastState = { shown: false };

  // --- Game loop ---
  base.registerGameLoop(
    (dt, sceneTime) => {
      for (const puzzle of puzzles) {
        puzzle.update(dt, sceneTime);
        levelState.set(puzzle.id, puzzle.getState());
      }

      // Spin the brass floor gears on scene time so the mechanism
      // reads as a working clockwork room instead of a still life.
      for (const g of animatedGears) {
        g.mesh.rotation.y = sceneTime * g.speed;
      }

      // Canon p.7 concurrent-puzzle discoverability: once-per-session
      // toast when Basil first strolls within interact range of the
      // gramophone.
      if (!gramoToastState.shown && gramophone.isInInteractRange(basil.mesh.position)) {
        gramoToastState.shown = true;
        gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Press , to wind the gramophone.",
        });
      }

      // Guardian Dog + Gramophone (canon p.7 concurrent-puzzle beat).
      // Both run off the same dt. The dog's probe considers whether
      // Minna is purring (CalmingResonance active) and whether either
      // cat is shadow-slipped — the canonical "how do I sneak past?"
      // primer teaches that Shadow Slip alone is not enough, but Purr
      // or the gramophone are.
      gramophone.tick(dt, sceneTime);
      guardianDog.tick(dt, [
        {
          position: minna.mesh.position,
          purring: calmingResonance.isActive,
          stealthed: shadowSlip.isActive,
        },
        {
          position: basil.mesh.position,
          purring: false,
          stealthed: false,
        },
      ]);

      const snapshot = tutorialGate.getReadabilitySnapshot();
      readabilityCues.update(snapshot, auraSight.isActive);
      updateTutorialCamera(cameraSystem, minna.mesh.position, basil.mesh.position, snapshot);
    },
    {
      isLevelComplete: () => levelState.get("tutorialGate")?.solved === true,
      onAbilityKey: (key, pressed, released) => {
        for (const puzzle of puzzles) {
          if (puzzle.onAbilityKey && puzzle.onAbilityKey(key, pressed, released)) {
            return true;
          }
        }
        // Basil interact ("," key) — wind the gramophone when in range.
        // Routed at the scene layer because the tutorial gate puzzle
        // doesn't own the gramophone; the Guardian Dog beat sits between
        // the plate/dial/crystal chain, not inside it.
        if (key === "," && pressed) {
          if (gramophone.isInInteractRange(basil.mesh.position)) {
            if (gramophone.play()) {
              return true;
            }
            // Already playing or cooling down — swallow the key so we
            // don't rebind it to anything else.
            return true;
          }
        }
        return false;
      },
    },
  );

  // Start ambient audio after a short delay
  setTimeout(() => audioManager.startAmbient(), 500);

  // Emit level started event
  gameEvents.emit(GameEvents.LEVEL_STARTED, "LiminalAtrium");

  // --- Opening cutscene ---
  const introLines = LEVEL_INTRO["LiminalAtrium"] ?? [];
  const openingSteps: CutsceneStep[] = [
    { type: "fade", fadeDirection: "in", duration: 1200, color: { r: 0, g: 0, b: 0 } },
    { type: "wait", duration: 400 },
    // Level title card
    ...introLines.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    { type: "wait", duration: 300 },
    // Character dialogue
    ...OPENING_CUTSCENE.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    { type: "wait", duration: 500 },
    // Controls are taught in play, not here: a one-line movement chip
    // after the cutscene and a cue above each step's object with just
    // that step's keys (TutorialGate + ui/tutorialCues.ts). This used to
    // be ~16 s of control text before the player could move.
    // Canon p.7 "concurrent puzzle" thesis — teach the gramophone +
    // sleeping-dog beat explicitly in the opening cutscene so the first
    // time the player approaches the rug they already know the mechanic.
    { type: "dialogue", speaker: "", text: CONTROL_HINTS.GUARDIAN_DOG_HINT, duration: 4000 },
    { type: "wait", duration: 300 },
  ];

  // Play opening cutscene after a brief delay to let the scene render a frame.
  // `playDeferred` (not raw setTimeout) so that `__startAtPuzzle` can cancel
  // this pending intro before the timer fires — see
  // docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  cutsceneSystem.playDeferred(openingSteps, 600);

  return {
    physicsSystem,
    cameraSystem,
    inputManager,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    audioManager,
    cutsceneSystem,
  };
}

// ─── Helper: Shadow Zone creation ────────────────────────────────────────

/**
 * Creates invisible box meshes that serve as shadow zones for Shadow Slip.
 * Placed beneath platforms where deep shadows would naturally gather.
 */
function createShadowZones(scene: Scene): ShadowZoneInfo[] {
  const zones: ShadowZoneInfo[] = [];

  // Zone positions come from LIMINAL_ATRIUM_LAYOUT.shadowZones so
  // the "shadow pools under the furniture" placements stay in sync
  // with the hero prop list. See LiminalAtriumLayout.ts.
  const zoneDefs = LIMINAL_ATRIUM_LAYOUT.shadowZones;

  for (let i = 0; i < zoneDefs.length; i++) {
    const def = zoneDefs[i];
    const mesh = MeshBuilder.CreateBox(
      `shadowZone_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene
    );
    mesh.position = def.position;
    mesh.isVisible = false; // Invisible trigger volume
    mesh.isPickable = false;
    tagAs(mesh, "placeholder");

    // Add a semi-transparent dark visual for debug/atmosphere
    const debugVisual = MeshBuilder.CreateBox(
      `shadowZoneVisual_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene
    );
    debugVisual.position = def.position;
    debugVisual.isVisible = WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS;
    debugVisual.isPickable = false;
    const darkMat = new StandardMaterial(`shadowZoneMat_${i}`, scene);
    darkMat.diffuseColor = new Color3(0.02, 0.01, 0.05);
    darkMat.emissiveColor = new Color3(0.01, 0.005, 0.02);
    darkMat.alpha = 0.4;
    debugVisual.material = darkMat;
    tagAs(debugVisual, "placeholder");

    zones.push({ mesh });
  }

  return zones;
}

function updateTutorialCamera(
  cameraSystem: CameraSystem,
  minnaPos: Vector3,
  basilPos: Vector3,
  snapshot: TutorialReadabilitySnapshot,
): void {
  const objectiveAnchor = (() => {
    switch (snapshot.step) {
      case "plate":
        return snapshot.pressurePlate.rootMesh.position.add(new Vector3(0, 0.8, 0));
      case "dial":
        return snapshot.dial.rootMesh.position.add(new Vector3(0, 0.8, 0));
      case "crystal":
        return snapshot.crystalReceiver.rootMesh.position.add(new Vector3(0, 0.9, 0));
      case "portal":
      case "complete":
        return snapshot.portal.rootMesh.position.add(new Vector3(0, 1.2, 0));
    }
  })();

  const playerMidY = (minnaPos.y + basilPos.y) * 0.5;
  const anchor = new Vector3(
    objectiveAnchor.x,
    Math.min(objectiveAnchor.y, playerMidY + 0.25),
    objectiveAnchor.z,
  );

  const catSeparation = Vector3.Distance(minnaPos, basilPos);
  const maxDistanceToAnchor = Math.max(
    Vector3.Distance(minnaPos, anchor),
    Vector3.Distance(basilPos, anchor),
  );
  const weightByStep = {
    plate: 0.24,
    dial: 0.28,
    crystal: 0.22,
    portal: 0.26,
    complete: 0,
  } as const;
  const allowBias = snapshot.step !== "complete" && catSeparation <= 8 && maxDistanceToAnchor <= 14;
  cameraSystem.setFocusAnchor(anchor, allowBias ? weightByStep[snapshot.step] : 0);
}

// ─── Helper: Readability / Affordance Layer ──────────────────────────────

function createLiminalReadabilityCues(
  scene: Scene,
  base: BaseLevelContext,
  objects: {
    platforms: Mesh[];
    pressurePlate: PressurePlate;
    dial: Dial;
    crystalReceiver: CrystalReceiver;
    portal: Portal;
    hasEnvironment: boolean;
  },
): LiminalReadabilityCues {
  const root = new TransformNode("atriumReadabilityRig", scene);

  const brass = Color3.FromHexString(COLORS.BRASS);
  const copper = Color3.FromHexString(COLORS.COPPER);
  const green = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
  const purple = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE);
  const darkWood = new Color3(0.18, 0.1, 0.06);

  // Walkable cues live in the world permanently, so keep their default
  // state subdued — dim self-lit brass inlay over a dark wood joist.
  // Lighting is disabled on both mats: the 4.5-intensity god-ray and the
  // green bio accent spots re-tinted the lit brass into saturated
  // yellow-green "laser lines" spanning the parlor (Q-023). Emissive
  // stays well under the 0.45 ceiling (Q-037/Q-039 palette lesson) and
  // every cue mesh is glow-excluded in createWalkableCue so the strips
  // never bloom into beacons.
  const glowLayer = base.getGlowLayer();
  const trimMat = makeCueMaterial(scene, "atrium_walkableTrim_mat", brass, 0.5, 0.08);
  trimMat.disableLighting = true;
  const woodMat = makeCueMaterial(scene, "atrium_walkablePlank_mat", darkWood, 0.55, 0.5);
  woodMat.disableLighting = true;
  // Conduits start fully suppressed (nearly invisible) and only light up
  // as the active puzzle step moves through them.
  const conduitSuppressedMat = makeCueMaterial(scene, "atrium_conduit_suppressed_mat", copper, 0.0, 0.0);
  const conduitHintMat = makeCueMaterial(scene, "atrium_conduit_hint_mat", copper, 0.18, 0.008);
  const conduitFocusMat = makeCueMaterial(scene, "atrium_conduit_focus_mat", brass, 0.82, 0.06);
  const conduitSolvedMat = makeCueMaterial(scene, "atrium_conduit_solved_mat", green, 0.92, 0.09);
  const ghostMat = makeCueMaterial(scene, "atrium_hiddenSocket_mat", green, 0.12, 0.03);
  const portalLockMat = makeCueMaterial(scene, "atrium_portalLock_mat", purple, 0.08, 0.03);
  const shadowMat = makeCueMaterial(scene, "atrium_naturalShadow_mat", new Color3(0.02, 0.01, 0.05), 0.18, 0.01);
  const scaffoldMat = makeCueMaterial(scene, "atrium_scaffold_mat", new Color3(0.16, 0.1, 0.08), 0.2, 0.006);
  const canvasMat = makeCueMaterial(scene, "atrium_canvas_mat", new Color3(0.18, 0.14, 0.16), 0.16, 0.004);

  // The imported sunroom already has window frames. The fallback's
  // oversized floating mullions otherwise cross the authored windows.
  if (!objects.hasEnvironment) createSunroomBackdrop(scene, root);

  // Procedural drapes/supports remain a missing-environment fallback.
  if (!objects.hasEnvironment) {
    createDrapedMass(scene, root, "atrium_drape_armchair", new Vector3(-6.2, 1.4, -1.6), 1.8, 1.4, canvasMat);
    createDrapedMass(scene, root, "atrium_drape_cabinet", new Vector3(-2.4, 1.9, -1.6), 2.3, 2.6, canvasMat);
    createDrapedMass(scene, root, "atrium_drape_crate", new Vector3(2.1, 1.1, -1.6), 1.4, 1.1, canvasMat);
  }

  // Walkable surfaces get a consistent dashed brass inlay so players can
  // distinguish real traversal geometry from background furniture.
  if (!objects.hasEnvironment) createWalkableCue(scene, root, "atrium_groundCue", new Vector3(0, 0.02, -0.72), ATRIUM.GROUND_WIDTH, trimMat, woodMat, glowLayer);
  for (const platform of objects.hasEnvironment ? [] : objects.platforms) {
    // Visible platforms have real legs (dressVisiblePlatform); the faint
    // scaffold is only for the ones the sunroom art stands in for.
    if (!platform.isVisible) {
      createPlatformSupport(scene, root, `${platform.name}_support`, platform.position, getMeshWorldWidth(platform), scaffoldMat);
    }
    createWalkableCue(
      scene,
      root,
      `${platform.name}_walkableCue`,
      new Vector3(platform.position.x, platform.position.y + 0.24, -0.72),
      getMeshWorldWidth(platform),
      trimMat,
      woodMat,
      glowLayer,
      platform.name === "platform5" ? 1.0 : 0.88,
    );
  }

  const platePos = objects.pressurePlate.rootMesh.position;
  const dialPos = objects.dial.rootMesh.position;
  const crystalPos = objects.crystalReceiver.rootMesh.position;
  const portalPos = objects.portal.rootMesh.position;

  const plateToDial = createConduit(scene, root, "atrium_conduit_plate_to_dial", [
    platePos.add(new Vector3(0.05, 0.25, -0.7)),
    new Vector3(5.6, 2.5, -0.7),
    new Vector3(6.8, 3.55, -0.7),
    dialPos.add(new Vector3(-0.35, 0.05, -0.7)),
  ], conduitSuppressedMat);

  const dialToCrystal = createConduit(scene, root, "atrium_conduit_dial_to_hidden_socket", [
    dialPos.add(new Vector3(-0.4, -0.2, -0.74)),
    new Vector3(3.5, 3.65, -0.74),
    new Vector3(-1.5, 2.85, -0.74),
    crystalPos.add(new Vector3(0.45, 0.25, -0.74)),
  ], conduitSuppressedMat);

  const crystalToPortal = createConduit(scene, root, "atrium_conduit_socket_to_portal", [
    crystalPos.add(new Vector3(-0.35, 0.28, -0.74)),
    new Vector3(-6.2, 2.6, -0.74),
    portalPos.add(new Vector3(0.45, -0.25, -0.74)),
  ], conduitSuppressedMat);

  // Conduits start disabled so Level 0 doesn't open with cyan spaghetti
  // stretched across the whole room. The readability update promotes
  // them to hint/focus as each puzzle step becomes active.
  plateToDial.setEnabled(false);
  dialToCrystal.setEnabled(false);
  crystalToPortal.setEnabled(false);

  // The controls have physical authored bodies. Flat alpha backplates were
  // visible as colored squares through the cats and are not needed here.

  const hiddenSocket = MeshBuilder.CreateTorus(
    "atrium_hiddenCrystal_socket",
    { diameter: 1.05, thickness: 0.04, tessellation: 32 },
    scene,
  );
  hiddenSocket.parent = root;
  hiddenSocket.position = crystalPos.add(new Vector3(0, 0.42, -0.62));
  hiddenSocket.rotation.x = Math.PI / 2;
  hiddenSocket.material = ghostMat;
  hiddenSocket.isPickable = false;
  hiddenSocket.isVisible = false;

  const portalLock = MeshBuilder.CreateTorus(
    "atrium_portal_dormant_lock",
    { diameter: 1.05, thickness: 0.035, tessellation: 32 },
    scene,
  );
  portalLock.parent = root;
  portalLock.position = portalPos.add(new Vector3(0, 0.75, -0.58));
  portalLock.rotation.x = Math.PI / 2;
  portalLock.material = portalLockMat;
  portalLock.isPickable = false;

  if (!objects.hasEnvironment) {
    createNaturalShadow(scene, root, "atrium_shadow_pool_plate", new Vector3(4, 1.25, -0.82), 3.2, 0.3, shadowMat);
    createNaturalShadow(scene, root, "atrium_shadow_pool_crystal", new Vector3(-5, 0.75, -0.82), 2.6, 0.24, shadowMat);
  }

  const cueLights = {
    plate: createCueLight(scene, "cue_plate_light", platePos.add(new Vector3(0, 1.0, -0.4)), brass, 0.9, 4.5),
    dial: createCueLight(scene, "cue_dial_light", dialPos.add(new Vector3(0, 0.9, -0.4)), brass, 0.45, 4.0),
    crystal: createCueLight(scene, "cue_hidden_crystal_light", crystalPos.add(new Vector3(0, 0.9, -0.4)), green, 0.28, 4.0),
    // The camera sees +Z faces; the old back light left the ornate frame black.
    portal: createCueLight(scene, "cue_portal_lock_light", portalPos.add(new Vector3(0, 1.2, 1.8)), new Color3(0.75, 0.65, 0.49), 0.32, 5.0),
  };

  // Classify the modular rig's source. Tags do not establish visual acceptance.
  tagAs(root, "modular-final");
  for (const child of root.getChildMeshes(false)) {
    tagAs(child, "modular-final");
  }

  const applyInteractiveState = (
    interactive: PressurePlate | Dial | CrystalReceiver | Portal,
    state: "suppressed" | "hint" | "focus" | "active" | "solved",
  ) => {
    interactive.setReadabilityState(state);
    if (state === "focus" || state === "active") {
      interactive.highlight();
    } else {
      interactive.unhighlight();
    }
  };

  const setConduitState = (
    conduit: Mesh,
    state: "suppressed" | "hint" | "focus" | "active" | "solved",
  ) => {
    // Only Aura Sight exposes the connection graph; ordinary play uses the
    // object's motion/light and the concise cue, not lines across the room.
    // AuraSight already draws the authored room's connection threads. Keep
    // this older routed graph only for the missing-environment fallback.
    conduit.setEnabled(!objects.hasEnvironment && state !== "suppressed" && base.auraSight.isActive);
    switch (state) {
      case "suppressed":
        conduit.material = conduitSuppressedMat;
        break;
      case "hint":
        conduit.material = conduitHintMat;
        break;
      case "focus":
      case "active":
        conduit.material = conduitFocusMat;
        break;
      case "solved":
        conduit.material = conduitSolvedMat;
        break;
    }
  };

  // Cue-light intensities kept low so the puzzle interactives themselves
  // stay the brightest thing in each frame. Previously these ran up to
  // 1.05 which made the cue dots compete with the objects they were
  // meant to point at.
  const lightStates = {
    suppressed: 0.03,
    hint: 0.1,
    focus: 0.32,
    active: 0.42,
    solved: 0.28,
  } as const;

  let time = 0;
  const beforeRender = () => {
    if ((scene.metadata as Record<string, any> | undefined)?.__forceLiminalReadabilityCrash) {
      throw new Error("Injected Liminal readability crash");
    }
    time += scene.getEngine().getDeltaTime() / 1000;
    const pulse = 0.5 + Math.sin(time * 2.4) * 0.5;
    const subtlePulse = 0.75 + pulse * 0.25;

    if (hiddenSocket.isVisible) {
      ghostMat.emissiveColor = green.scale(0.04 + pulse * 0.08);
      hiddenSocket.scaling.setAll(subtlePulse);
    } else {
      ghostMat.emissiveColor = green.scale(0.01);
      hiddenSocket.scaling.setAll(0.88);
    }
    portalLockMat.emissiveColor = purple.scale(0.02 + pulse * 0.04);
    portalLock.rotation.z += scene.getEngine().getDeltaTime() / 1000 * 0.35;
  };
  const disposeBeforeRender = base.registerSafeBeforeRender("liminalReadabilityRig", beforeRender);
  base.addDisposeCallback(disposeBeforeRender);

  return {
    update(snapshot, auraSightActive) {
      let plateState: keyof typeof lightStates = "suppressed";
      let dialState: keyof typeof lightStates = "suppressed";
      let crystalState: keyof typeof lightStates = "suppressed";
      let portalState: keyof typeof lightStates = "suppressed";

      switch (snapshot.step) {
        case "plate":
          plateState = "focus";
          dialState = "hint";
          setConduitState(plateToDial, "suppressed");
          setConduitState(dialToCrystal, "suppressed");
          setConduitState(crystalToPortal, "suppressed");
          applyInteractiveState(objects.pressurePlate, "focus");
          applyInteractiveState(objects.dial, "hint");
          applyInteractiveState(objects.crystalReceiver, "suppressed");
          applyInteractiveState(objects.portal, "suppressed");
          break;
        case "dial":
          plateState = "active";
          dialState = "focus";
          crystalState = "suppressed";
          setConduitState(plateToDial, "focus");
          setConduitState(dialToCrystal, "suppressed");
          setConduitState(crystalToPortal, "suppressed");
          applyInteractiveState(objects.pressurePlate, "active");
          applyInteractiveState(objects.dial, "focus");
          applyInteractiveState(objects.crystalReceiver, "suppressed");
          applyInteractiveState(objects.portal, "suppressed");
          break;
        case "crystal":
          plateState = "suppressed";
          dialState = "active";
          crystalState = "focus";
          portalState = "suppressed";
          setConduitState(plateToDial, "suppressed");
          setConduitState(dialToCrystal, "focus");
          setConduitState(crystalToPortal, "suppressed");
          applyInteractiveState(objects.pressurePlate, "suppressed");
          applyInteractiveState(objects.dial, "active");
          applyInteractiveState(objects.crystalReceiver, "focus");
          applyInteractiveState(objects.portal, "suppressed");
          break;
        case "portal":
        case "complete":
          plateState = "suppressed";
          dialState = "suppressed";
          crystalState = "active";
          portalState = snapshot.step === "complete" ? "active" : "focus";
          setConduitState(plateToDial, "suppressed");
          setConduitState(dialToCrystal, "suppressed");
          setConduitState(crystalToPortal, "focus");
          applyInteractiveState(objects.pressurePlate, "suppressed");
          applyInteractiveState(objects.dial, "suppressed");
          applyInteractiveState(objects.crystalReceiver, "active");
          applyInteractiveState(objects.portal, snapshot.step === "complete" ? "active" : "focus");
          break;
      }

      const crystalVisible = auraSightActive || snapshot.step === "crystal" || snapshot.portalOpen;
      hiddenSocket.isVisible = crystalVisible && auraSightActive;
      portalLock.isVisible = auraSightActive && !snapshot.portalOpen;
      ghostMat.alpha = crystalVisible ? 0.18 : 0.02;
      portalLockMat.alpha = portalState === "focus" || portalState === "active" ? 0.16 : 0.05;

      cueLights.plate.intensity = lightStates[plateState];
      cueLights.dial.intensity = lightStates[dialState];
      cueLights.crystal.intensity = lightStates[crystalState];
      cueLights.portal.intensity = ({
        suppressed: 0.02,
        hint: 0.08,
        focus: 0.92,
        active: 1.1,
        solved: 0.62,
      } as const)[portalState];
    },
  };
}

function makeCueMaterial(
  scene: Scene,
  name: string,
  color: Color3,
  alpha: number,
  emissiveScale: number,
): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.diffuseColor = color;
  material.emissiveColor = color.scale(emissiveScale);
  material.specularColor = Color3.Black();
  material.alpha = alpha;
  return material;
}

function createWalkableCue(
  scene: Scene,
  parent: TransformNode,
  name: string,
  position: Vector3,
  width: number,
  trimMat: StandardMaterial,
  woodMat: StandardMaterial,
  glowLayer: GlowLayer,
  alpha = 0.65,
): void {
  const plank = MeshBuilder.CreateBox(`${name}_plank`, { width, height: 0.08, depth: 0.08 }, scene);
  plank.parent = parent;
  plank.position = position.add(new Vector3(0, -0.06, -0.01));
  plank.material = woodMat;
  plank.visibility = alpha;
  plank.isPickable = false;
  glowLayer.addExcludedMesh(plank);

  // Short inlay dashes instead of one continuous brass lip — a full-width
  // strip read as a laser beam across the parlor (Q-023). The first and
  // last dash stay flush with the plank ends so the walkable extent the
  // cue encodes is unchanged. Merged into a single mesh per cue to avoid
  // scattering dozens of alpha-blended draw calls (Q-039 lesson).
  const dashCount = Math.max(3, Math.round(width / 1.1));
  const dashWidth = Math.min(0.55, (width / dashCount) * 0.5);
  const step = (width - dashWidth) / (dashCount - 1);
  const dashes: Mesh[] = [];
  for (let i = 0; i < dashCount; i++) {
    const dash = MeshBuilder.CreateBox(`${name}_dash_${i}`, { width: dashWidth, height: 0.035, depth: 0.08 }, scene);
    dash.position = position.add(new Vector3(-width / 2 + dashWidth / 2 + i * step, 0.02, 0));
    dashes.push(dash);
  }
  const inlay = Mesh.MergeMeshes(dashes, true);
  if (inlay) {
    inlay.name = `${name}_brassInlay`;
    inlay.parent = parent;
    inlay.material = trimMat;
    inlay.visibility = alpha;
    inlay.isPickable = false;
    glowLayer.addExcludedMesh(inlay);
  }
}

function getMeshWorldWidth(mesh: Mesh): number {
  const boundingBox = mesh.getBoundingInfo().boundingBox;
  return boundingBox.extendSizeWorld.x * 2;
}

function createConduit(
  scene: Scene,
  parent: TransformNode,
  name: string,
  path: Vector3[],
  material: StandardMaterial,
): Mesh {
  const conduit = MeshBuilder.CreateTube(
    name,
    { path, radius: 0.035, tessellation: 8 },
    scene,
  );
  conduit.parent = parent;
  conduit.material = material;
  conduit.isPickable = false;
  return conduit;
}

function createNaturalShadow(
  scene: Scene,
  parent: TransformNode,
  name: string,
  position: Vector3,
  width: number,
  height: number,
  material: StandardMaterial,
): void {
  const shadow = MeshBuilder.CreateBox(name, { width, height, depth: 0.025 }, scene);
  shadow.parent = parent;
  shadow.position = position;
  shadow.material = material;
  shadow.isPickable = false;
}

function createSunroomBackdrop(
  scene: Scene,
  parent: TransformNode,
): void {
  const frameMat = new PBRMaterial("atrium_frameMat", scene);
  frameMat.albedoColor = new Color3(0.12, 0.13, 0.15);
  frameMat.metallic = 0.2;
  frameMat.roughness = 0.8;
  const frameBump = getOrMakeNoiseBump(scene, "atrium_iron", 0.5);
  frameBump.uScale = 4;
  frameBump.vScale = 4;
  frameMat.bumpTexture = frameBump;

  const glassMat = new PBRMaterial("atrium_glassMat", scene);
  glassMat.albedoColor = new Color3(0.08, 0.11, 0.13);
  glassMat.metallic = 0.5;
  glassMat.roughness = 0.3;
  glassMat.alpha = 0.3;
  glassMat.transparencyMode = PBRMaterial.MATERIAL_ALPHABLEND;

  const beamTop = MeshBuilder.CreateBox("atrium_window_beam_top", { width: 23, height: 0.16, depth: 0.08 }, scene);
  beamTop.parent = parent;
  beamTop.position = new Vector3(0, 6.55, -1.05);
  beamTop.material = frameMat;
  beamTop.visibility = 1.0;
  beamTop.isPickable = false;

  const beamMid = MeshBuilder.CreateBox("atrium_window_beam_mid", { width: 23, height: 0.12, depth: 0.08 }, scene);
  beamMid.parent = parent;
  beamMid.position = new Vector3(0, 4.5, -1.05);
  beamMid.material = frameMat;
  beamMid.visibility = 0;
  beamMid.isPickable = false;

  const bayCenters = [-7.8, 0, 7.8];
  for (let i = 0; i < bayCenters.length; i++) {
    const bayX = bayCenters[i];
    const glass = MeshBuilder.CreateBox(`atrium_window_glass_${i}`, { width: 6.2, height: 3.9, depth: 0.03 }, scene);
    glass.parent = parent;
    glass.position = new Vector3(bayX, 5.5, -1.12);
    glass.material = glassMat;
    glass.visibility = 0.15;
    glass.isPickable = false;

    const leftPost = MeshBuilder.CreateBox(`atrium_window_left_post_${i}`, { width: 0.14, height: 4.15, depth: 0.08 }, scene);
    leftPost.parent = parent;
    leftPost.position = new Vector3(bayX - 3.2, 5.48, -1.05);
    leftPost.material = frameMat;
    leftPost.visibility = 0.6;
    leftPost.isPickable = false;

    const rightPost = MeshBuilder.CreateBox(`atrium_window_right_post_${i}`, { width: 0.14, height: 4.15, depth: 0.08 }, scene);
    rightPost.parent = parent;
    rightPost.position = new Vector3(bayX + 3.2, 5.48, -1.05);
    rightPost.material = frameMat;
    rightPost.visibility = 0.6;
    rightPost.isPickable = false;

    const centerPost = MeshBuilder.CreateBox(`atrium_window_center_post_${i}`, { width: 0.12, height: 4.0, depth: 0.08 }, scene);
    centerPost.parent = parent;
    centerPost.position = new Vector3(bayX, 5.48, -1.05);
    centerPost.material = frameMat;
    centerPost.visibility = 0.5;
    centerPost.isPickable = false;
  }
}

function createDrapedMass(
  scene: Scene,
  parent: TransformNode,
  name: string,
  position: Vector3,
  width: number,
  height: number,
  material: StandardMaterial,
): void {
  const body = MeshBuilder.CreateBox(`${name}_body`, { width, height, depth: 0.2 }, scene);
  body.parent = parent;
  body.position = position;
  body.material = material;
  body.visibility = 0.45;
  body.isPickable = false;

  const drape = MeshBuilder.CreatePlane(`${name}_front`, { width: width + 0.15, height: height + 0.2 }, scene);
  drape.parent = parent;
  drape.position = position.add(new Vector3(0, -0.05, 0.11));
  drape.material = material;
  drape.visibility = 0.45;
  drape.isPickable = false;
}

/**
 * Brass band along a platform's front face plus a rivet near each end,
 * flush with the face (not above the top, so it never reads as a lip the
 * cats' paws sink into), and two wooden legs with brass feet under the
 * back edge. A leg that would pass through a lower platform slides inward
 * past it (and is dropped if that leaves its own platform); LEG_X pins
 * legs that must dodge set dressing. None of this has a collider.
 */
/**
 * Leg x positions that must dodge set dressing or read cleanly:
 * - platform3's west leg would stand inside the gramophone (x 1.84-3.04 at
 *   the back of the room);
 * - platform5 overhangs platform1, so its east leg sits well clear of
 *   platform1's west end instead of grazing its corner.
 */
const LEG_X: Record<string, [number, number]> = {
  platform3: [3.25, 5.7],
  platform5: [-10.7, -7.6],
};

function dressVisiblePlatform(
  scene: Scene,
  plat: Mesh,
  def: PlatformDef,
  woodMat: PBRMaterial,
  material: PBRMaterial,
  allPlatforms: Mesh[],
): void {
  const top = plat.position.y + platformHeight(def) / 2;
  const bottom = plat.position.y - platformHeight(def) / 2;
  const front = plat.position.z + platformDepth(def) / 2;
  const back = plat.position.z - platformDepth(def) / 2;
  const LEG = 0.14;
  const left = plat.position.x - def.w / 2;
  const right = plat.position.x + def.w / 2;
  for (const side of [-1, 1]) {
    let x = LEG_X[plat.name]?.[side < 0 ? 0 : 1] ?? plat.position.x + side * (def.w / 2 - 0.3);
    // Keep the leg out of any lower platform it would pierce: slide it
    // toward this platform's middle, past the blocker.
    for (const other of allPlatforms) {
      if (other === plat) continue;
      const bb = other.getBoundingInfo().boundingBox;
      if (bb.maximumWorld.y > bottom || x + LEG / 2 < bb.minimumWorld.x || x - LEG / 2 > bb.maximumWorld.x) continue;
      x = side < 0 ? bb.maximumWorld.x + LEG : bb.minimumWorld.x - LEG;
    }
    if (x - LEG / 2 < left || x + LEG / 2 > right) continue;
    const leg = MeshBuilder.CreateBox(`${plat.name}_leg_${side > 0 ? "e" : "w"}`, { width: LEG, height: bottom, depth: LEG }, scene);
    leg.position.set(x, bottom / 2, back + 0.15);
    leg.material = woodMat;
    leg.isPickable = false;
    leg.receiveShadows = true;
    tagAs(leg, "modular-final");
    const foot = MeshBuilder.CreateCylinder(`${plat.name}_legFoot_${side > 0 ? "e" : "w"}`, { height: 0.06, diameter: 0.22, tessellation: 12 }, scene);
    foot.position.set(x, 0.03, back + 0.15);
    foot.material = material;
    foot.isPickable = false;
    tagAs(foot, "modular-final");
  }
  const band = MeshBuilder.CreateBox(`${plat.name}_brassBand`, { width: def.w + 0.02, height: 0.1, depth: 0.03 }, scene);
  band.position.set(plat.position.x, top - 0.08, front + 0.012);
  band.material = material;
  band.isPickable = false;
  band.receiveShadows = true;
  tagAs(band, "modular-final");
  for (const side of [-1, 1]) {
    const rivet = MeshBuilder.CreateCylinder(
      `${plat.name}_brassRivet_${side > 0 ? "e" : "w"}`,
      { height: 0.04, diameter: 0.13, tessellation: 12 },
      scene,
    );
    rivet.rotation.x = Math.PI / 2;
    rivet.position.set(plat.position.x + side * Math.max(def.w / 2 - 0.22, 0.1), top - 0.08, front + 0.03);
    rivet.material = material;
    rivet.isPickable = false;
    tagAs(rivet, "modular-final");
  }
}

function createPlatformSupport(
  scene: Scene,
  parent: TransformNode,
  name: string,
  platformPosition: Vector3,
  width: number,
  material: StandardMaterial,
): void {
  const postHeight = platformPosition.y - 0.2;
  const leftX = platformPosition.x - width * 0.35;
  const rightX = platformPosition.x + width * 0.35;
  const y = postHeight / 2;

  for (const [suffix, x] of [["left", leftX], ["right", rightX]] as const) {
    const post = MeshBuilder.CreateBox(`${name}_${suffix}_post`, { width: 0.16, height: postHeight, depth: 0.08 }, scene);
    post.parent = parent;
    post.position = new Vector3(x, y, -0.92);
    post.material = material;
    post.visibility = 0.7;
    post.isPickable = false;
  }

  const beam = MeshBuilder.CreateBox(`${name}_beam`, { width: width + 0.15, height: 0.12, depth: 0.08 }, scene);
  beam.parent = parent;
  beam.position = new Vector3(platformPosition.x, platformPosition.y - 0.28, -0.92);
  beam.material = material;
  beam.visibility = 0.6;
  beam.isPickable = false;
}

function createCueLight(
  scene: Scene,
  name: string,
  position: Vector3,
  color: Color3,
  intensity: number,
  range: number,
): PointLight {
  const light = new PointLight(name, position, scene);
  light.diffuse = color;
  light.specular = color.scale(0.6);
  light.intensity = intensity;
  light.range = range;
  return light;
}
